import type { FlagName } from '@platform/feature-flags';

type LegacyRedirectEntry = {
  source: string;
  target: (domain: string) => string;
};

/**
 * Legacy paths that nginx routes to the SPA but are not yet served by it.
 * Each entry redirects to the external customer-self-care app.
 *
 * Remove an entry once its SPA page is built — the new route will match
 * before the notFoundComponent ever checks this map.
 */
export const LEGACY_REDIRECTS: LegacyRedirectEntry[] = [
  // `/account` itself is owned by the SPA Konto layout — don't re-add it here.
  { source: '/account/abo', target: (d) => `https://customer-self-care.${d}/ui/my_subscription` },
  { source: '/account/daten', target: (d) => `https://customer-self-care.${d}/ui/account` },
  { source: '/account/einstellungen', target: (d) => `https://customer-self-care.${d}/ui/settings` },
  { source: '/account/zugangsdaten', target: (d) => `https://customer-self-care.${d}/ui/account_credentials#zugangsdaten` },
  { source: '/account/zahlungsdaten', target: (d) => `https://customer-self-care.${d}/ui/account_payment_data#zahlungsdaten` },
  { source: '/account/kundendaten', target: (d) => `https://customer-self-care.${d}/ui/account_personal_data#persoenliche-daten` },
  { source: '/account/email', target: (d) => `https://customer-self-care.${d}/ui/account/email` },
  { source: '/account/passwort', target: (d) => `https://customer-self-care.${d}/ui/request_reset_password` },
  { source: '/account/dunning', target: (d) => `https://customer-self-care.${d}/ui/dunning/payment-change` },
  { source: '/abolaufzeit', target: (d) => `https://customer-self-care.${d}/ui/cancel/subscription` },
  { source: '/netflixaktivieren', target: (d) => `https://customer-self-care.${d}/ui/link-netflix-account` },
  { source: '/netflixkontofinden', target: (d) => `https://customer-self-care.${d}/ui/recover-netflix-account` },
];

/**
 * Landing page of the legacy customer-self-care app. Back target for routes declaring
 * `backBehavior: 'legacy'` — pages the user reached from the old app, whose parent
 * surface the SPA doesn't own.
 */
export const legacySelfCareHome = (domain: string) => `https://customer-self-care.${domain}/ui/home`;

export const LegacyPaths = {
  subscription: '/account/abo',
  credentials: '/account/daten',
  settings: '/account/einstellungen',
  accessCredentials: '/account/zugangsdaten',
  payment: '/account/zahlungsdaten',
  personalData: '/account/kundendaten',
  email: '/account/email',
  password: '/account/passwort',
  dunning: '/account/dunning',
  cancelSubscription: '/abolaufzeit',
  linkNetflix: '/netflixaktivieren',
  recoverNetflix: '/netflixkontofinden',
} as const;

export const legacyRedirectMap = new Map<string, LegacyRedirectEntry['target']>(
  LEGACY_REDIRECTS.map((entry) => [entry.source, entry.target]),
);

/**
 * Bookmark redirects that switch target based on a feature flag.
 *
 * When the flag is on, a `/jugendschutz` bookmark goes to the new Hub page;
 * when off, it falls back to the legacy CSC URL. Add an entry here only when
 * a Hub destination genuinely needs a flag-gated bookmark fallback — most
 * legacy paths belong in {@link LEGACY_REDIRECTS}.
 */
export interface FlaggedLegacyRedirect {
  source: string;
  flag: FlagName;
  /** Hub-internal canonical path navigated via TanStack Router. */
  whenOn: string;
  /** External fallback URL when the flag is off. */
  whenOff: (domain: string) => string;
}

export const FLAGGED_LEGACY_REDIRECTS: readonly FlaggedLegacyRedirect[] = [
  {
    source: '/jugendschutz',
    flag: 'page-parental-controls',
    whenOn: '/account/parental-controls',
    whenOff: (d) => `https://customer-self-care.${d}/ui/fsk?tenant=waipu`,
  },
];

export const flaggedLegacyRedirectMap = new Map<string, FlaggedLegacyRedirect>(
  FLAGGED_LEGACY_REDIRECTS.map((entry) => [entry.source, entry]),
);
